import { toTypedSchema } from "@vee-validate/zod";
import { z } from "zod";

import { i18n } from "@/i18n";

const { t } = i18n.global;

export const businessSubscriptionRequestLanguages = {
  english: t("le-business-subscription-request-modal.form.languages.options.english"),
  german: t("le-business-subscription-request-modal.form.languages.options.german"),
  danish: t("le-business-subscription-request-modal.form.languages.options.danish"),
  french: t("le-business-subscription-request-modal.form.languages.options.french"),
  spanish: t("le-business-subscription-request-modal.form.languages.options.spanish"),
  swedish: t("le-business-subscription-request-modal.form.languages.options.swedish"),
  norwegian: t("le-business-subscription-request-modal.form.languages.options.norwegian"),
  arabic: t("le-business-subscription-request-modal.form.languages.options.arabic"),
  other: t("le-business-subscription-request-modal.form.languages.options.other")
} as const;

const allowedLanguages = Object.keys(businessSubscriptionRequestLanguages) as [
  keyof typeof businessSubscriptionRequestLanguages
];

export const businessSubscriptionRequestSubjects = {
  trade: t("le-business-subscription-request-modal.form.subjects.options.trade"),
  business: t("le-business-subscription-request-modal.form.subjects.options.business"),
  tech: t("le-business-subscription-request-modal.form.subjects.options.tech"),
  law: t("le-business-subscription-request-modal.form.subjects.options.law"),
  economy: t("le-business-subscription-request-modal.form.subjects.options.economy"),
  medicine: t("le-business-subscription-request-modal.form.subjects.options.medicine"),
  construction: t("le-business-subscription-request-modal.form.subjects.options.construction"),
  mechanical: t("le-business-subscription-request-modal.form.subjects.options.mechanical"),
  environment: t("le-business-subscription-request-modal.form.subjects.options.environment"),
  chemistry: t("le-business-subscription-request-modal.form.subjects.options.chemistry"),
  clinical: t("le-business-subscription-request-modal.form.subjects.options.clinical"),
  other: t("le-business-subscription-request-modal.form.subjects.options.other")
} as const;

const allowedSubjects = Object.keys(businessSubscriptionRequestSubjects) as [
  keyof typeof businessSubscriptionRequestSubjects
];

const BusinessSubscriptionRequest = z.object({
  type: z.enum(["individual", "flexible", "professional"]).optional(),
  name: z
    .string({
      required_error: t("validation.required")
    })
    .max(255, { message: t("validation.max", { max: 255 }) })
    .nonempty({ message: t("validation.required") }),
  email: z
    .string({
      required_error: t("validation.required")
    })
    .max(255, { message: t("validation.max", { max: 255 }) })
    .nonempty({ message: t("validation.required") })
    .email(t("validation.email")),
  phone: z
    .union([
      z
        .string()
        .min(8, { message: t("validation.min", { min: 8 }) })
        .max(20, { message: t("validation.max", { max: 20 }) }),
      z.string().length(0) // Allow empty string without clashing with min/max
    ])
    .optional(),
  company: z
    .string({
      required_error: t("validation.required")
    })
    .max(255, { message: t("validation.max", { max: 255 }) })
    .nonempty({ message: t("validation.required") }),
  accountQty: z.string().optional(),
  languages: z.array(z.enum(allowedLanguages)).optional(),
  subjects: z.array(z.enum(allowedSubjects)).optional(),
  specializedDictionaries: z.boolean(),
  comment: z
    .string()
    .max(1000, { message: t("validation.max", { max: 1000 }) })
    .optional()
});

export type BusinessSubscriptionRequest = z.infer<typeof BusinessSubscriptionRequest>;

export const businessSubscriptionRequestFormValidator = toTypedSchema(BusinessSubscriptionRequest);
